<template>
  <div class="language-switcher">
    <Button type="button" class="language-button p-button-text" @click="togglePanel">
      <div class="language-option">
        <img :src="selectedLanguage.flag" class="flag-icon" />
        <span>{{ selectedLanguage.name }}</span>
      </div>
    </Button>

    <OverlayPanel ref="op" appendTo="body" :baseZIndex="10000">
      <div class="language-options">
        <div
          v-for="lang in languages"
          :key="lang.id"
          class="language-option"
          @click="selectLanguage(lang)"
          :class="{'selected': selectedLanguage.id === lang.id}"
        >
          <img :src="lang.flag" class="flag-icon" />
          <span>{{ lang.name }}</span>
        </div>
      </div>
    </OverlayPanel>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { setLocale } from '@/i18n'
import englishFlag from '@/assets/images/flags/en.png'
import persianFlag from '@/assets/images/flags/fa.png'

export default {
  name: 'LanguageSwitcher',
  setup() {
    const { t, locale } = useI18n()
    const op = ref(null);

    const languages = [
      {
        id: 'en',
        name: t('languages.english'),
        flag: englishFlag,
      },
      {
        id: 'fa',
        name: t('languages.persian'),
        flag: persianFlag
      }
    ]

    // Initialize with default language
    const selectedLanguage = ref(languages.find(lang => lang.id === locale.value) || languages[0])

    // Add a computed property for current language name
    const currentLocaleName = computed(() => {
      return locale.value === 'fa' ? 'فارسی' : 'English'
    })

    onMounted(() => {
      // Set selected language based on current locale
      const currentLang = languages.find(lang => lang.id === locale.value)
      if (currentLang) {
        selectedLanguage.value = currentLang
      }
    })

    const togglePanel = (event) => {
      op.value.toggle(event);
    }

    const selectLanguage = (lang) => {
      selectedLanguage.value = lang;
      setLocale(lang.id);

      // Update language names when locale changes
      languages.forEach(l => {
        if (l.id === 'en') {
          l.name = t('languages.english')
        } else if (l.id === 'fa') {
          l.name = t('languages.persian')
        }
      })

      op.value.hide();
    }

    return {
      languages,
      selectedLanguage,
      currentLocaleName,
      op,
      togglePanel,
      selectLanguage
    }
  }
}
</script>

<style scoped>
.language-switcher {
  display: inline-block;
  position: relative;
}

.language-button {
  padding: 0.5rem;
  border: none;
  background: transparent;
  cursor: pointer;
}

.language-options {
  min-width: 150px;
}

.language-option {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  padding: 8px;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.language-option:hover {
  background-color: #f5f5f5;
}

.language-option.selected {
  background-color: #e6f7ff;
}

.flag-icon {
  width: 20px;
  height: 15px;
  object-fit: cover;
}

/* RTL support for dropdown when in Persian mode */
:deep([dir="rtl"]) .language-option {
  flex-direction: row-reverse;
}
</style>
